import { useMemo, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import UnderlineTabNav from '@/components/UnderlineTabNav'
import { DrawerShell, SearchInput } from '@/tableControls'
import { foldForSearch } from '@/utils/foldForSearch'
import type { CaseDetail } from '@/services/projections'
import { allowedDrivers } from './engine/schema'
import { parseNumeric, type CaseInputs, type SchemaEntry, type View } from './engine'
import { DRIVER_LABELS } from './gridClasses'
import { formatByUnit } from './projectionsFormat'

const VIEW_TABS = [
  { id: 'river_oaks', label: 'River Oaks' },
  { id: 'highland_park', label: 'Highland Park' },
  { id: 'ga', label: 'G&A' },
  { id: 'consolidated', label: 'Consolidated' },
]

type HoldProjectedValuesDialogProps = {
  schema: SchemaEntry[]
  draft: CaseInputs
  selectedCase: CaseDetail
  onApply: (view: View, lineKeys: string[]) => void | Promise<void>
  onClose: () => void
}

type LineRow = {
  key: string
  label: string
  eligible: boolean
  reason?: string
  preview?: string
  driverLabel?: string
}


function hasPreRollValue(preRoll: CaseDetail['pre_rollforward_outputs'], view: View, line: string): boolean {
  const row = preRoll?.[view]?.[line]
  if (!row) {
    return false
  }
  for (let i = 0; i < 36; i++) {
    if (row[i] != null && row[i] !== '') {
      return true
    }
  }
  return false
}

function ineligibleReason(
  schema: SchemaEntry[],
  entry: SchemaEntry,
  view: View,
  draft: CaseInputs,
  preRoll: CaseDetail['pre_rollforward_outputs'],
): string | null {
  if (
    entry.computed ||
    ['subtotal', 'metric', 'header', 'check'].includes(entry.kind)
  ) {
    return 'computed line'
  }
  const drivers = allowedDrivers(schema, entry.key, view, draft.consolidated_only, draft)
  if (drivers.length === 0) {
    return 'computed line'
  }
  const valueDrivers = drivers.filter((d) => d === 'dollar_value' || d === 'count_value')
  if (valueDrivers.length === 0) {
    return 'driver fixed by schema'
  }
  if (!hasPreRollValue(preRoll, view, entry.key)) {
    return 'no pre-roll values'
  }
  return null
}

function buildRows(
  schema: SchemaEntry[],
  view: View,
  draft: CaseInputs,
  preRoll: CaseDetail['pre_rollforward_outputs'],
): LineRow[] {
  const lines = schema.filter(
    (entry) =>
      entry.kind === 'input' ||
      entry.computed ||
      ['subtotal', 'metric', 'header', 'check'].includes(entry.kind),
  )
  return lines.map((entry) => {
    const reason = ineligibleReason(schema, entry, view, draft, preRoll)
    const eligible = reason == null
    const active = draft.drivers[view]?.[entry.key]?.active
    const driverLabel =
      active && active in DRIVER_LABELS
        ? DRIVER_LABELS[active as keyof typeof DRIVER_LABELS]
        : undefined
    let preview: string | undefined
    if (eligible && preRoll?.[view]?.[entry.key]) {
      const raw = preRoll[view]![entry.key]!
      const parts: string[] = []
      for (let i = 0; i < 3; i++) {
        const cell = raw[i] ?? null
        const n = cell == null ? null : parseNumeric(cell)
        parts.push(formatByUnit(entry.unit, typeof n === 'number' ? n : null))
      }
      preview = parts.join(', ')
    }
    return {
      key: entry.key,
      label: entry.label,
      eligible,
      reason: reason ?? undefined,
      preview,
      driverLabel,
    }
  })
}

export default function HoldProjectedValuesDialog({
  schema,
  draft,
  selectedCase,
  onApply,
  onClose,
}: HoldProjectedValuesDialogProps) {
  const [view, setView] = useState<View>('river_oaks')
  const [query, setQuery] = useState('')
  const [checked, setChecked] = useState<Set<string>>(() => new Set())

  const preRoll = selectedCase.pre_rollforward_outputs

  const rows = useMemo(
    () => buildRows(schema, view, draft, preRoll),
    [schema, view, draft, preRoll],
  )

  const foldedQuery = foldForSearch(query)
  const filtered = rows.filter((row) => {
    if (!foldedQuery) {
      return true
    }
    return (
      foldForSearch(row.label).includes(foldedQuery) ||
      foldForSearch(row.key).includes(foldedQuery)
    )
  })

  const checkedInView = [...checked].filter((key) => rows.some((r) => r.key === key && r.eligible))

  return (
    <DrawerShell title="Hold projected values" onClose={onClose}>
      <UnderlineTabNav tabs={VIEW_TABS} activeId={view} onChange={(id) => setView(id as View)} />
      <div className="mt-3">
        <SearchInput value={query} onChange={setQuery} placeholder="Search lines" />
      </div>
      <ul className="mt-3 max-h-[50vh] space-y-1 overflow-y-auto text-sm">
        {filtered.map((row) => (
          <li
            key={row.key}
            className={`flex items-start gap-2 rounded px-2 py-1.5 ${row.eligible ? 'text-gray-900' : 'text-gray-400'}`}
          >
            <input
              type="checkbox"
              className="mt-1"
              disabled={!row.eligible}
              checked={checked.has(row.key)}
              onChange={(event) => {
                setChecked((prev) => {
                  const next = new Set(prev)
                  if (event.target.checked) {
                    next.add(row.key)
                  } else {
                    next.delete(row.key)
                  }
                  return next
                })
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{row.label}</div>
              {row.eligible ? (
                <div className="text-xs text-gray-500">
                  {row.driverLabel ? `${row.driverLabel} · ` : ''}
                  {row.preview}
                </div>
              ) : (
                <div className="text-xs">{row.reason}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={checkedInView.length === 0}
          onClick={() => {
            void (async () => {
              await onApply(view, checkedInView)
              onClose()
            })()
          }}
        >
          Apply
        </Button>
      </div>
    </DrawerShell>
  )
}
